/**
 * honestyRatio — the live federation honesty ratio, fetched for the public
 * /honesty page (honest marketing: "here's the real world").
 *
 * Source of truth is the public aggregate endpoint `GET /introspection/honesty`
 * (CO 242): unauthenticated BY DESIGN, returning ONLY the aggregate
 * `{ percent, passed, total, grandfathered }` — never per-unit chain rows —
 * single-sourced server-side with the same pipeline the gated introspection
 * MCP tool uses, so the public number can't diverge from the internal one.
 *
 * (Previously this called the `introspection_system_coverage_gaps` MCP tool
 * over `POST /tools/<tool>` and recomputed passed/total client-side; CO 141
 * gated that tool behind `introspection:read`, and the public introspection UI
 * has since been deprecated. The aggregate route is the public contract.)
 */

const GATEWAY_URL =
  import.meta.env.VITE_FEDERATION_GATEWAY_URL ||
  "https://mcp.comos-gateway.com";

/** What the page renders. A no-data state is represented, not thrown. */
export interface HonestyRatio {
  /** Whole-number percent, e.g. 67. Null when there's no chain to measure. */
  percent: number | null;
  /** Total artifacts in the proof chain. */
  total: number;
  /** Artifacts that have passed QC. */
  passed: number;
  /** Artifacts still grandfathered (admitted without a passing closing test). */
  grandfathered: number;
  /** Present when there is no chain to show (honest empty state). */
  reason?: string;
}

/** Fetch the live aggregate honesty ratio, federation-wide. */
export async function fetchHonestyRatio(): Promise<HonestyRatio> {
  const res = await fetch(`${GATEWAY_URL}/introspection/honesty`);
  if (!res.ok) {
    throw new Error(`honesty-ratio fetch failed: ${res.status}`);
  }
  return (await res.json()) as HonestyRatio;
}
